import React, { useState, useEffect, useRef } from 'react';
import { Router, Route, Switch, withRouter } from 'react-router-dom';
import { useDispatch, useSelector, useStore } from 'react-redux';
import GlobalStyles from '@mui/material/GlobalStyles';
import LoginPage from './LoginPage';
import PerformRouter from './nav/Router';
import PerformAdminRouter from './nav/AdminRouter';
import PerformBuilders from './nav/Builders';
import Policy from './nav/Policy';
import UserLogout from './user/UserLogout';
import 'global/utils/support';
import { history, APP_ACTIONS_KEY } from 'global/utils/nativeShell';
import NativeWebReady from 'global/components/NativeWebReady';
import { getAlerts } from 'redux/actions/view';
import AppThemeProvider from 'global/components/app-theme-provider';
import { get } from 'global/lib/lodash';
import { getUserAppPermissions } from 'global/utils';
import { getUserPermissions } from 'redux/actions/userLogin';
import SelfRegister from './user/SelfRegister';
import { registerTimer } from 'global/utils/activityTimer';
import { useUser } from 'global/hooks/useUser';
import { isDark } from 'global/styles';
import { LoadAccountSettings } from './LoadAccontSettings';
import { savePostLoginRedirect, shouldBounceToLogout } from 'global/utils/loginRedirect';
import HashAccessBanner from './share/HashAccessBanner';
import HashViewGuard from './share/HashViewGuard';
import ShareRedemptionRoute from './share/ShareRedemptionRoute';
import MessagePdfPage from './share/MessagePdfPage';
import { useHashView } from './share/useHashView';
import { parseShareUrl, SHARE_ROUTE_PATH, MESSAGE_SHARE_ROUTE_PATH } from './share/shareLinkConfig';

registerTimer();

const FILAMENT_ACCESS_MATCH = 'filament.access';
const ADMIN_PERMISSION = 'manageAccount';

const userIsAdmin = (user) => {
  const appPermissions = getUserAppPermissions(get(user, 'permissions', []));
  return !!(appPermissions || []).some((permission) => permission.value === ADMIN_PERMISSION);
};

const shouldIgnoreUserForAnalytics = (user) => {
  const realmId = get(user, 'realm.id');
  const email = get(user, 'email', '').toLowerCase();
  const permissions = get(user, 'permissions', []);

  if (realmId === 70689) {
    return true;
  }

  if (realmId !== 1 && email.includes('@ibacos.com')) {
    return true;
  }

  return permissions.some((permission) => get(permission, 'name', '').indexOf(FILAMENT_ACCESS_MATCH) > -1);
};

if (!Array.prototype.at) {
  function at(n) {
    // ToInteger() abstract op
    n = Math.trunc(n) || 0;
    // Allow negative indexing from the end
    if (n < 0) n += this.length;
    // OOB access is guaranteed to return undefined
    if (n < 0 || n >= this.length) return undefined;
    // Otherwise, this is just normal property access
    return this[n];
  }

  const TypedArray = Reflect.getPrototypeOf(Int8Array);
  for (const C of [Array, String, TypedArray]) {
    Object.defineProperty(C.prototype, 'at', { value: at, writable: true, enumerable: false, configurable: true });
  }
}

const appGlobalStyles = (theme) => ({
  html: {
    scrollbarColor: '#888 transparent',
    backgroundColor: theme.palette.background.default,
    color: theme.palette.text.primary,

    '& a': {
      color: isDark(theme) ? theme.palette.common.white : undefined,
    },
  },
});

const AppWithRouter = withRouter((props) => {
  const [title, changeTitle] = useState(<div>Perform</div>);
  const { me, loggedIn, userRealmTier } = useUser();
  const accessToken = useSelector((state) => get(state, 'auth.accessToken'));
  const reduxStore = useStore();
  const { hasToken: hashAuthHasToken } = useHashView();
  const dispatch = useDispatch();
  const loginInitRef = useRef(false);
  const gtagLoginFiredRef = useRef(false);
  const userId = get(me, 'id');
  const userRealmId = get(me, 'realm.id');
  const userEmail = get(me, 'email', '');
  const userPermissions = get(me, 'permissions');
  const userGroupNames = get(me, 'userGroups', [])
    .map((userGroup) => get(userGroup, 'name'))
    .filter(Boolean)
    .join(', ');

  useEffect(() => {
    window[APP_ACTIONS_KEY] = window[APP_ACTIONS_KEY] || {};
    window[APP_ACTIONS_KEY].getAuth = () => {
      try {
        return JSON.parse(JSON.stringify(reduxStore.getState().auth || {}));
      } catch (err) {
        return reduxStore.getState().auth;
      }
    };
  }, [reduxStore]);

  useEffect(() => {
    if (process.env.NODE_ENV === 'production') {
      const scripta = document.createElement('script');
      scripta.async = true;
      scripta.src = 'https://www.googletagmanager.com/gtag/js?id=G-KQGEE7JKYT';

      const scriptb = document.createElement('script');
      scriptb.async = true;
      scriptb.text = "window.dataLayer = window.dataLayer || [];function gtag(){dataLayer.push(arguments);}window.gtag = gtag;gtag('js', new Date());gtag('config', 'G-KQGEE7JKYT');";

      document.head.appendChild(scripta);
      document.head.appendChild(scriptb);
    }
  }, []);

  useEffect(() => {
    if (props.location && process.env.NODE_ENV === 'production') {
      window.gtag('config', 'G-KQGEE7JKYT', {
        page_title: props.location.pathname,
        page_path: props.location.pathname,
      });
    }
  }, [props.location]);

  useEffect(() => {
    if (loggedIn || accessToken) {
      if (!loginInitRef.current) {
        loginInitRef.current = true;
        dispatch(getAlerts());
        dispatch(getUserPermissions());
      }
      return;
    }

    loginInitRef.current = false;
    gtagLoginFiredRef.current = false;

    // Logged-out `/logout` must not bounce to `/logout` again — that reload
    // loop covered native login with a spinner that only a storage wipe cleared.
    const pathname = window.location.pathname;
    const isShareLink = !!parseShareUrl(pathname);
    if (
      shouldBounceToLogout(pathname) &&
      !isShareLink &&
      !hashAuthHasToken
    ) {
      // PFM-2565: remember the originally-requested URL so we can return the user there after login
      savePostLoginRedirect(window.location);
      window.location.href = '/logout';
    }
  }, [loggedIn, accessToken, hashAuthHasToken, dispatch]);

  useEffect(() => {
    if (!loggedIn || process.env.NODE_ENV !== 'production' || !userId) {
      return;
    }

    window.gtag('set', 'userId', userId);
    window.gtag('set', 'user_properties', {
      user_realm_id: userRealmId,
      user_ignore: shouldIgnoreUserForAnalytics(me),
      user_is_admin: userIsAdmin(me),
      user_tier: userRealmTier,
      user_groups: userGroupNames,
    });

    if (!gtagLoginFiredRef.current) {
      gtagLoginFiredRef.current = true;
      window.gtag('event', 'login', {
        event_category: 'engagement',
        event_label: 'login',
      });
    }
  }, [loggedIn, userId, userRealmId, userEmail, userPermissions, userRealmTier, userGroupNames]);

  return (
    <>
      <GlobalStyles styles={appGlobalStyles} />
      <HashAccessBanner />
      <HashViewGuard>
        <Switch>
          <Route exact path={MESSAGE_SHARE_ROUTE_PATH} component={MessagePdfPage} />
          <Route exact path={SHARE_ROUTE_PATH} component={ShareRedemptionRoute} />
          <Route exact path="/" component={LoginPage} />
          <Route path={['/profile', '/alerts', '/users', '/account']} render={(props) => <PerformAdminRouter {...props} title={title} changeTitle={changeTitle} hide={false} />} />
          <Route exact path="/builders" component={PerformBuilders} />
          <Route exact path="/logout" component={UserLogout} />
          <Route exact path="/register/:id" component={SelfRegister} />
          <Route exact path="/register/:id/:tier" component={SelfRegister} />
          <Route exact path={'/home'} render={(props) => <PerformRouter {...props} title={title} changeTitle={changeTitle} hide={false} />} />
          <Route exact path={['/policy', '/policy/:type']} component={Policy} />
          <Route path={'/:base'} render={(props) => <PerformRouter {...props} title={title} changeTitle={changeTitle} hide={false} />} />
        </Switch>
      </HashViewGuard>
    </>
  );
});

export default function App() {
  return (
    <AppThemeProvider>
      <Router history={history}>
        <NativeWebReady />
        <AppWithRouter />
        <LoadAccountSettings />
      </Router>
    </AppThemeProvider>
  );
}
